import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import WiseAPI from 'wise-api';
import IStartConferenceDTO from 'wise-api/lib/resources/session/dtos/IStartConferenceDTO';

import { ThemeContext } from 'App';
import { iStore } from 'domain/interfaces/models';
import { JoinConference } from 'domain/usecases/conference/remote';
import { disconnectSocket, initializeSocket } from 'infra/socket/SackSocket';
import { makeRemoteJoinConference } from 'main/factories/usecases/conference/JoinConferenceFactory';
import { makeRemoteLeaveConference } from 'main/factories/usecases/conference/LeaveConferenceFactory';
import { makeReduxActiveMessage } from 'main/factories/usecases/message/Update';
import { History } from 'main/routes';
import RequestWaitingRoom from 'presentation/components/messages/requestWaitingRoom';
import { Sidebar } from 'presentation/components/sidebar';
import { closeModal } from 'utils/closeModal';
import { Container } from './styles/styledConf';

export interface ParamsState {
  appointmentId: string;
  m?: boolean;
  c?: boolean;
  u?: string;
  t?: string;
}

export interface QueryState {
  appointmentId: string;
}

export const Conf: React.FC = (): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(true);
  const { theme } = useContext(ThemeContext);

  const { search } = useLocation();
  const { push } = useHistory();

  const params = useMemo(
    () => ({
      m: new URLSearchParams(search).get('m') ?? false,
      c: new URLSearchParams(search).get('c') ?? false,
      appointmentId: new URLSearchParams(search).get('appointmentId'),
      t: new URLSearchParams(search).get('t') ?? null,
      u: new URLSearchParams(search).get('u') ?? null,
      s: new URLSearchParams(search).get('sessionName') ?? null,
      sr: new URLSearchParams(search).get('sr') ?? null,
      ak: new URLSearchParams(search).get('ak') ?? undefined,
    }),
    [search],
  );
  console.log('>>>params', params);
  const { m, c, appointmentId, t, u, s, sr, ak } = params;

  const { conference } = useSelector((store: iStore) => store);
  const authRef = useRef<iStore['auth']>();
  const { auth } = useSelector((store: iStore) => store);
  authRef.current = auth;

  const hasJoinedRef = useRef(false);
  const timeoutMeet: { current: NodeJS.Timeout | null } = useRef(null);

  useEffect(() => {
    if (sr) initializeSocket(Number(sr));

    return () => {
      disconnectSocket();
    };
  }, [sr]);

  useEffect(() => {
    if (hasJoinedRef.current) return;
    hasJoinedRef.current = true;

    let cancelled = false;

    const start = async (): Promise<void> => {
      try {
        const response =
          s !== null
            ? ({ sessionName: s } as JoinConference.Model)
            : await makeRemoteJoinConference().join({
                conferenceShort: String(t),
              });

        if (cancelled) return;

        const { sessionName } = response;

        const foundConference = conference?.records?.find(
          item => item.short === params.t,
        );

        const buttons = [
          'microphone',
          'camera',
          'closedcaptions',
          'desktop',
          'fullscreen',
          'fodeviceselection',
          'hangup',
          'profile',
          'chat',
          'sharedvideo',
          'settings',
          'raisehand',
          'videoquality',
          'filmstrip',
          'invite',
          'feedback',
          'stats',
          'shortcuts',
          'tileview',
          'videobackgroundblur',
          'download',
          'help',
          'avatar',
          'libras',
          'transcription',
          'reactions',
          'participants-pane',
          'noisesuppression',
          'security',
          'select-background',
          'toggle-camera',
          'polls',
        ];

        if (theme.systemName === 'FESTin') {
          buttons.push('participants-pane');
        }

        if (response?.moderator && response?.recording)
          buttons.push(
            ...[
              'recording',
              'livestreaming',
              'mute-everyone',
              'mute-video-everyone',
            ],
          );

        if (response?.moderator && !response?.recording)
          makeReduxActiveMessage().active({
            active: 'error',
            title: 'Permissão de gravação',
            content: 'Organização não permite gravações de conferência.',
            actionOk: () => closeModal(),
            actionCancel: () => closeModal(),
          });

        console.log(
          '>>> Debug baseURL: ',
          window.config.connection.sessionManagerUrl,
        );
        console.log('>>> Debug domain: ', window.config.connection.confUrl);

        if (cancelled) return;

        const meetEl = document.getElementById('meet');
        if (meetEl) {
          const iframes = meetEl.querySelectorAll('iframe');
          iframes.forEach(iframe => iframe.remove());
        }

        const v4h = await WiseAPI({
          baseUrl: `${window.config.connection.sessionManagerUrl}/api`,
          domain: `${window.config.connection.confUrl}`,
        });

        if (cancelled) return;

        await v4h.session.startConference(sessionName, {
          parentNode: document.getElementById('meet'),
          userInfo: {
            displayName: u ?? 'Convidado',
          },
          shareLink: `${window.location.origin}/${
            foundConference?.type === 'RESTRICT' ? 'waitingRoom' : 'preconf'
          }?t=${String(t)}`,
          startWithAudioMuted: !(m === 'true'),
          startWithVideoMuted: !(c === 'true'),
          buttons: buttons as unknown as IStartConferenceDTO['buttons'],
          onClose: () => {
            if (sr) {
              if (authRef.current?.access?.token?.length) {
                makeReduxActiveMessage().active({
                  active: 'finishAppointmentSAC',
                  actionOk: () => {
                    push(`/sac/list`);
                    closeModal();
                  },
                  data: {
                    request: sr,
                  },
                  actionCancel: () => closeModal(),
                });

                return;
              }

              push(`/leave?t=${t}&u=${u}&c=${c}&m=${m}&sr=${sr}&ak=${ak}`);

              return;
            }

            push(`/leave?t=${t}&u=${u}&c=${c}&m=${m}`);
          },
          moderator: response.moderator,
          onModeratorReject: () => {
            push(`/leave?t=${t}&u=${u}&c=${c}&m=${m}${sr ? `&sr=${sr}` : ''}`);
          },
          logo: theme.images.logo.opacity,
        });

        if (!cancelled) setLoading(false);
      } catch (error) {
        console.log('>>>error', error);
        if (!cancelled) {
          setLoading(false);
          History.getHistory().push('/home');
        }
      }
    };

    start();

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
      if (timeoutMeet?.current) clearTimeout(timeoutMeet.current);
      if (appointmentId) console.log('### left');
      if (t && !sr) makeRemoteLeaveConference().leave({ conferenceShort: t });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container id="meet">
      {loading && <div>loading...</div>}
      {!loading && sr && <Sidebar />}
      <RequestWaitingRoom />
    </Container>
  );
};

export default Conf;
